import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Caption1,
  Tab,
  TabList,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Add16Regular, Checkmark12Filled, ChevronUpDown16Regular, Mail16Regular, Settings16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useSurfaceLocation } from "./settings-url";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField } from "@/components/app-dialog";
import { FuiButton, FuiDropdown } from "@/components/fui-control";
import { useMockPlanAccess } from "@/features/access/mock-plan-access";
import { WorkspaceDangerPanel } from "@/features/access/WorkspaceDangerPanel";
import { WorkspaceAccessPanel, WorkspaceAuditPanel, WorkspaceInvitePanel } from "@/features/access/WorkspaceManagePanels";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { ReceivedInvitesDialog } from "@/features/inbox/InboxRows";
import { leaveWorkspace, leaveWorkspaceImpact, useWorkspaceDanger, workspaceDangerTab } from "@/features/access/workspace-danger";
import { DEFAULT_TEAM_ICON, TEAM_COLOR_PRESETS } from "@/features/resources/team-appearance";
import { WORKSPACE_MEMBERSHIP_LABEL, splitWorkspacesByMembership, workspaceRowTitle } from "@/features/workspace/workspace-face";
import { WorkspaceMark, WorkspaceProfileFields } from "@/features/workspace/workspace-mark";
import type { WorkspaceMembership, WorkspaceTemplate } from "@/engine/workspace-seeds";
import { useSettingsHub } from "@/shell/settings-hub";
import { pathnameOpensPlan } from "@/shell/project-nav";
import { useCatalogStore } from "@/store/catalog-store";
import { listWorkspaceSummaries } from "@/store/workspace-ops";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: 1,
    width: "100%",
  },
  btnWrap: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: 1,
    width: "100%",
    height: "32px",
    boxSizing: "border-box",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: "transparent",
    border: "none",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationNormal,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  btnWrapOpen: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    width: "100%",
    minWidth: 0,
    height: "100%",
    padding: "0 8px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    color: tokens.colorNeutralForeground1,
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  name: {
    flex: "1 1 auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "0",
    color: "var(--nav-ink)",
  },
  markSlot: {
    display: "flex",
    flexShrink: 0,
  },
  role: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    width: "14px",
    height: "14px",
  },
  count: {
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground3,
  },
  menuList: {
    minWidth: "240px",
    maxWidth: "300px",
    maxHeight: "360px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  generalContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "16px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  actionStatus: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actionSavedCheck: {
    color: tokens.colorPaletteGreenForeground1,
  },
  actionButtonGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginLeft: "auto",
  },
  templateCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  leave: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "16px",
    paddingTop: "12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  shell: {
    display: "flex",
    flex: 1,
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    gap: "12px",
  },
  tabs: {
    flex: "0 0 140px",
    height: "100%",
    gap: "4px",
    paddingTop: "4px",
  },
  tab: {
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "32px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightRegular,
    "& .fui-Tab__content": {
      fontWeight: tokens.fontWeightRegular,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      whiteSpace: "nowrap",
    },
    "&::before, &::after": {
      content: "none",
    },
    "&[aria-selected='true']": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    "&[aria-selected='true']:hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  pane: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: "auto",
    paddingTop: "4px",
    paddingLeft: "20px",
  },
});

function leaveOrphanProject() {
  const projectId = useProjectStore.getState().activeId;
  if (projectId && !useCatalogStore.getState().projects[projectId]) {
    useProjectStore.getState().closeProject();
    return true;
  }
  return false;
}

export function WorkspaceMenu({ children, onOpenChange }: { children: ReactElement; onOpenChange?: (open: boolean) => void }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useSurfaceLocation();

  const [wsOpen, setWsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const pendingInvites = useMockPlanAccess().incoming.length;

  const activeWsId = useCatalogStore((s) => s.activeWorkspaceId);
  const workspaces = useCatalogStore((s) => s.workspaces);
  const activeWs = workspaces[activeWsId];
  const wsItems = useMemo(() => listWorkspaceSummaries(workspaces), [workspaces]);
  const { owned, joined, hasDivider } = splitWorkspacesByMembership(wsItems);

  const workspaceRow = (ws: (typeof wsItems)[number]) => (
    <MenuItemRadio
      key={ws.id}
      name="workspace"
      value={ws.id}
      icon={<WorkspaceMark icon={ws.icon} color={ws.color} imageUrl={ws.imageUrl} size={16} />}
      secondaryContent={<span className={styles.role}>{WORKSPACE_MEMBERSHIP_LABEL[ws.role]}</span>}
      title={workspaceRowTitle(ws.name, ws.role, ws.ownerName, ws.ownerEmail)}
    >
      {ws.name}
    </MenuItemRadio>
  );

  const goHomeIfNeeded = () => {
    if (pathnameOpensPlan(location.pathname) && leaveOrphanProject()) navigate("/");
    else navigate({ pathname: "/", hash: "" });
  };

  const onSwitchWs = (id: string) => {
    if (id === activeWsId) return;
    if (useCatalogStore.getState().switchWorkspace(id)) goHomeIfNeeded();
    setWsOpen(false);
  };

  if (!activeWs) return null;

  return (
    <>
      <Menu
        open={wsOpen}
        positioning={{ position: "below", align: "start" }}
        onOpenChange={(_, data) => {
          setWsOpen(data.open);
          onOpenChange?.(data.open);
        }}
        checkedValues={{ workspace: [activeWsId] }}
        onCheckedValueChange={(_, data) => {
          if (data.name !== "workspace") return;
          const id = data.checkedItems[0];
          if (id) onSwitchWs(id);
        }}
      >
        <MenuTrigger disableButtonEnhancement>{children}</MenuTrigger>
        <MenuPopover>
          <MenuList className={styles.menuList} aria-label="워크스페이스 목록">
            {owned.map(workspaceRow)}
            {hasDivider ? <MenuDivider /> : null}
            {joined.map(workspaceRow)}
            <MenuDivider />
            {pendingInvites > 0 ? (
              <MenuItem
                icon={<Mail16Regular />}
                secondaryContent={<span className={styles.count}>{pendingInvites > 9 ? "9+" : pendingInvites}</span>}
                onClick={() => {
                  setWsOpen(false);
                  setInvitesOpen(true);
                }}
              >
                받은 초대
              </MenuItem>
            ) : null}
            <MenuItem
              icon={<Add16Regular />}
              onClick={() => {
                setWsOpen(false);
                setCreating(true);
              }}
            >
              새 워크스페이스
            </MenuItem>
            <MenuItem
              icon={<Settings16Regular />}
              onClick={() => {
                setWsOpen(false);
                useSettingsHub.getState().open("workspace", "general");
              }}
            >
              워크스페이스 설정
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      <WorkspaceFormDialog
        open={creating}
        mode="create"
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          goHomeIfNeeded();
        }}
      />
      <ReceivedInvitesDialog open={invitesOpen} onClose={() => setInvitesOpen(false)} />
    </>
  );
}

export function WorkspaceSwitcher({ labelsVisible }: { labelsVisible: boolean }) {
  const styles = useStyles();
  const activeWs = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]);
  const pendingInvites = useMockPlanAccess().incoming.length;
  const [wsOpen, setWsOpen] = useState(false);

  if (!activeWs || !labelsVisible) return null;

  return (
    <div className={styles.container}>
      <div className={mergeClasses(styles.btnWrap, wsOpen && styles.btnWrapOpen)}>
        <WorkspaceMenu onOpenChange={setWsOpen}>
          <button
            type="button"
            className={styles.trigger}
            aria-label={
              pendingInvites > 0
                ? `워크스페이스 전환: ${activeWs.name}. 받은 초대 ${pendingInvites}건`
                : `워크스페이스 전환: ${activeWs.name}`
            }
            title={activeWs.name}
            aria-haspopup="menu"
          >
            <span className={styles.markSlot}>
              <WorkspaceMark icon={activeWs.icon} color={activeWs.color} imageUrl={activeWs.imageUrl} size={20} pending={pendingInvites > 0} />
            </span>
            <span className={styles.name}>{activeWs.name}</span>
            <ChevronUpDown16Regular className={styles.chevron} />
          </button>
        </WorkspaceMenu>
      </div>
    </div>
  );
}

const TEMPLATE_OPTIONS: Array<{ value: WorkspaceTemplate; label: string }> = [
  { value: "empty", label: "빈 워크스페이스" },
  { value: "erp", label: "웨이브엑스 SI 목업" },
  { value: "robot", label: "AI 로봇 공장 목업" },
  { value: "marketing", label: "그로스 마케팅 목업" },
];

type WorkspaceFormValue = {
  name: string;
  icon: string;
  color: string;
  imageUrl: string | null;
  description: string;
  template: WorkspaceTemplate;
};

function emptyForm(index: number): WorkspaceFormValue {
  const preset = TEAM_COLOR_PRESETS[index % TEAM_COLOR_PRESETS.length] ?? TEAM_COLOR_PRESETS[0]!;
  return { name: "", icon: DEFAULT_TEAM_ICON, color: preset.value, imageUrl: null, description: "", template: "empty" };
}

function saveWorkspaceFace(id: string, current: { name: string; icon: string; color: string; imageUrl: string | null; description: string }, next: WorkspaceFormValue) {
  const name = next.name.trim() || current.name;
  const description = next.description.trim();
  if (name === current.name && next.icon === current.icon && next.color === current.color && next.imageUrl === current.imageUrl && description === current.description) return;
  useCatalogStore.getState().updateWorkspace(id, { name, icon: next.icon, color: next.color, imageUrl: next.imageUrl, description });
}

export function WorkspaceGeneralSettings({
  active,
  role,
  canEdit,
}: {
  active: { id: string; name: string; icon: string; color: string; imageUrl: string | null; description?: string };
  role: string;
  canEdit: boolean;
}) {
  const styles = useStyles();
  const [name, setName] = useState(active.name);
  const [description, setDescription] = useState(active.description || "");
  const [icon, setIcon] = useState(active.icon);
  const [color, setColor] = useState(active.color);
  const [imageUrl, setImageUrl] = useState<string | null>(active.imageUrl);
  const [savedNote, setSavedNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState<(() => void) | null>(null);

  useEffect(() => {
    setName(active.name);
    setDescription(active.description || "");
    setIcon(active.icon);
    setColor(active.color);
    setImageUrl(active.imageUrl);
    setSavedNote(false);
  }, [active.id, active.name, active.description, active.icon, active.color, active.imageUrl]);

  const trimmed = name.trim();
  const dirty =
    trimmed !== active.name ||
    description.trim() !== (active.description || "") ||
    icon !== active.icon ||
    color !== active.color ||
    imageUrl !== active.imageUrl;

  const nameInvalid = trimmed.length === 0;
  const canSave = canEdit && dirty && !nameInvalid && !busy;

  const handleSave = () => {
    if (!canSave) return;
    setBusy(true);
    useCatalogStore.getState().updateWorkspace(active.id, {
      name: trimmed,
      description: description.trim(),
      icon,
      color,
      imageUrl,
    });
    setSavedNote(true);
    setBusy(false);
  };

  const handleReset = () => {
    setName(active.name);
    setDescription(active.description || "");
    setIcon(active.icon);
    setColor(active.color);
    setImageUrl(active.imageUrl);
    setSavedNote(false);
  };

  useEffect(() => {
    useSettingsHub.getState().setCloseGuard(() => {
      if (dirty) {
        setLeaveConfirm(() => () => useSettingsHub.getState().close());
        return false;
      }
      return true;
    });
    useSettingsHub.getState().setSectionGuard((next, nextScope) => {
      if (dirty && (next !== "general" || nextScope !== "workspace")) {
        setLeaveConfirm(() => () => useSettingsHub.getState().commitSection(next, nextScope));
        return false;
      }
      return true;
    });
    return () => {
      useSettingsHub.getState().setCloseGuard(null);
      useSettingsHub.getState().setSectionGuard(null);
    };
  }, [dirty]);

  return (
    <div className={styles.generalContainer}>
      <WorkspaceProfileFields
        name={name}
        description={description}
        icon={icon}
        color={color}
        imageUrl={imageUrl}
        readOnly={!canEdit}
        nameError={dirty && nameInvalid ? "워크스페이스 이름을 입력해 주세요." : null}
        roleLabel={WORKSPACE_MEMBERSHIP_LABEL[role as WorkspaceMembership] || role}
        onNameChange={(val) => {
          setName(val);
          setSavedNote(false);
        }}
        onDescriptionChange={(val) => {
          setDescription(val);
          setSavedNote(false);
        }}
        onIconChange={(val) => {
          setIcon(val);
          setSavedNote(false);
        }}
        onColorChange={(val) => {
          setColor(val);
          setSavedNote(false);
        }}
        onImageChange={(val) => {
          setImageUrl(val);
          setSavedNote(false);
        }}
        onEnterSubmit={canSave ? handleSave : undefined}
      />

      {canEdit ? (
        <div className={styles.actionsBar}>
          <div className={styles.actionStatus}>
            {savedNote && !dirty ? (
              <>
                <Checkmark12Filled className={styles.actionSavedCheck} />
                <span>변경사항이 저장되었습니다.</span>
              </>
            ) : dirty ? (
              <span>수정된 내용이 있습니다.</span>
            ) : null}
          </div>
          <div className={styles.actionButtonGroup}>
            <FuiButton
              appearance="secondary"
              density="dialog"
              disabled={!dirty || busy}
              onClick={handleReset}
            >
              취소
            </FuiButton>
            <FuiButton
              appearance="primary"
              density="dialog"
              disabled={!canSave}
              onClick={handleSave}
            >
              변경사항 저장
            </FuiButton>
          </div>
        </div>
      ) : null}

      <WorkspaceLeaveNote role={role} />

      <ConfirmDialog
        open={leaveConfirm != null}
        title="변경사항을 버릴까요?"
        message="저장하지 않은 워크스페이스 설정이 사라집니다."
        confirmLabel="버리기"
        cancelLabel="머무르기"
        onCancel={() => setLeaveConfirm(null)}
        onConfirm={() => {
          handleReset();
          const action = leaveConfirm;
          setLeaveConfirm(null);
          action?.();
        }}
      />
    </div>
  );
}

export function WorkspaceSettingsPane({ section }: { section: string }) {
  const active = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]);
  const role = useEffectiveWorkspaceRole();
  const canEdit = canWorkspace(role, "workspace.settings.update");
  if (!active) return null;
  return (
    <>
      {section === "general" ? (
        <WorkspaceGeneralSettings active={active} role={role} canEdit={canEdit} />
      ) : null}
      {section === "invites" ? <WorkspaceInvitePanel workspaceName={active.name} /> : null}
      {section === "access" ? <WorkspaceAccessPanel /> : null}
      {section === "audit" ? <WorkspaceAuditPanel /> : null}
      {section === "danger" ? <WorkspaceDangerPanel workspaceName={active.name} role={role} /> : null}
    </>
  );
}

function WorkspaceFormDialog({
  open,
  mode,
  onClose,
  onCreated,
}: {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onCreated?: () => void;
}) {
  const styles = useStyles();
  const active = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]);
  const count = useCatalogStore((s) => Object.keys(s.workspaces).length);
  const role = useEffectiveWorkspaceRole();
  const canEdit = canWorkspace(role, "workspace.settings.update");
  const canInvite = canWorkspace(role, "member.invite");
  const canAudit = canWorkspace(role, "audit.read");
  const showDanger = workspaceDangerTab(role);
  const [tab, setTab] = useState("general");
  useEffect(() => {
    if (!open) setTab("general");
  }, [open]);
  const tabs = [
    { value: "general", label: "일반" },
    ...(canInvite ? [{ value: "invites", label: "초대" }, { value: "access", label: "접근" }] : []),
    ...(canAudit ? [{ value: "audit", label: "감사 로그" }] : []),
    ...(showDanger ? [{ value: "danger", label: "위험 구역" }] : []),
  ];
  const tabValue = tabs.some((item) => item.value === tab) ? tab : "general";
  const valueRef = useRef<WorkspaceFormValue>(emptyForm(0));
  const initial: WorkspaceFormValue =
    mode === "edit" && active
      ? { name: active.name, icon: active.icon, color: active.color, imageUrl: active.imageUrl, description: active.description, template: active.template }
      : emptyForm(count);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={mode === "create" ? "새 워크스페이스" : "워크스페이스 설정"}
      description={mode === "create" ? "워크스페이스끼리는 플랜·인력·작업이 공유되지 않습니다." : undefined}
      width={mode === "edit" ? "lg" : "md"}
      height={mode === "edit" ? 520 : undefined}
      footer={
        mode === "create" ? (
          <DialogActionsBar
            cancelLabel="취소"
            confirmLabel="만들기"
            onCancel={onClose}
            onConfirm={() => {
              const v = valueRef.current;
              useCatalogStore.getState().createWorkspace({
                name: v.name || "새 워크스페이스",
                icon: v.icon,
                color: v.color,
                imageUrl: v.imageUrl,
                description: v.description,
                template: v.template,
              });
              onCreated?.();
              onClose();
            }}
          />
        ) : undefined
      }
    >
      {open && mode === "edit" && (
        <div className={styles.shell}>
          <TabList
            className={styles.tabs}
            vertical
            size="small"
            selectedValue={tabValue}
            aria-label="워크스페이스 설정"
            onTabSelect={(_, data) => {
              if (typeof data.value === "string") setTab(data.value);
            }}
          >
            {tabs.map((item) => (
              <Tab key={item.value} className={styles.tab} value={item.value}>
                {item.label}
              </Tab>
            ))}
          </TabList>
          <div className={styles.pane}>
            {tabValue === "general" ? (
              <WorkspaceFormFields
                key={`${mode}-${active?.id ?? "new"}`}
                initial={initial}
                showTemplate={false}
                readOnly={!canEdit}
                onChange={(next) => {
                  valueRef.current = next;
                  if (!canEdit || !active) return;
                  saveWorkspaceFace(active.id, active, next);
                }}
              />
            ) : null}
            {tabValue === "invites" ? <WorkspaceInvitePanel workspaceName={active?.name ?? "워크스페이스"} /> : null}
            {tabValue === "access" ? <WorkspaceAccessPanel /> : null}
            {tabValue === "audit" ? <WorkspaceAuditPanel /> : null}
            {tabValue === "danger" ? <WorkspaceDangerPanel workspaceName={active?.name ?? "워크스페이스"} role={role} /> : null}
          </div>
        </div>
      )}
      {open && mode === "create" && (
        <WorkspaceFormFields
          key={`${mode}-${active?.id ?? "new"}`}
          initial={initial}
          showTemplate
          onChange={(next) => {
            valueRef.current = next;
          }}
        />
      )}
    </AppDialog>
  );
}

function WorkspaceLeaveNote({ role }: { role: string }) {
  const styles = useStyles();
  const danger = useWorkspaceDanger();
  const [open, setOpen] = useState(false);
  if (role !== "editor" && role !== "viewer") return null;
  return (
    <>
      <div className={styles.leave}>
        {danger.departed ? (
          <Caption1>이 워크스페이스에서 나갔습니다. {leaveWorkspaceImpact()}</Caption1>
        ) : (
          <>
            <Caption1>이 워크스페이스에서 나갑니다. {leaveWorkspaceImpact()}</Caption1>
            <FuiButton appearance="outline" density="dialog" onClick={() => setOpen(true)}>
              나가기
            </FuiButton>
          </>
        )}
      </div>
      <ConfirmDialog
        open={open}
        title="워크스페이스 나가기"
        message={`이 워크스페이스에서 나갈까요? ${leaveWorkspaceImpact()}`}
        confirmLabel="나가기"
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          leaveWorkspace(role);
          setOpen(false);
        }}
      />
    </>
  );
}

function WorkspaceFormFields({
  initial,
  showTemplate,
  readOnly = false,
  onChange,
}: {
  initial: WorkspaceFormValue;
  showTemplate: boolean;
  readOnly?: boolean;
  onChange: (v: WorkspaceFormValue) => void;
}) {
  const styles = useStyles();
  const [name, setName] = useState(initial.name);
  const [icon, setIcon] = useState(initial.icon);
  const [color, setColor] = useState(initial.color);
  const [imageUrl, setImageUrl] = useState(initial.imageUrl);
  const [description, setDescription] = useState(initial.description);
  const [template, setTemplate] = useState<WorkspaceTemplate>(initial.template);

  useEffect(() => {
    onChange({ name, icon, color, imageUrl, description, template });
  }, [name, icon, color, imageUrl, description, template, onChange]);

  return (
    <div className={styles.form}>
      <WorkspaceProfileFields
        name={name}
        description={description}
        icon={icon}
        color={color}
        imageUrl={imageUrl}
        readOnly={readOnly}
        autoFocusName={showTemplate && !readOnly}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onIconChange={setIcon}
        onColorChange={setColor}
        onImageChange={setImageUrl}
      />
      {showTemplate ? (
        <div className={styles.templateCard}>
          <DialogField label="시작 데이터">
            <FuiDropdown density="dialog" value={template} options={TEMPLATE_OPTIONS} onValueChange={(next) => setTemplate(next as WorkspaceTemplate)} />
          </DialogField>
        </div>
      ) : null}
    </div>
  );
}
